import { RecruitmentStatus } from "./schema";
import { CheckCircle2, AlertTriangle, Users } from "lucide-react";

/**
 * Recruitment Status Copy Configuration
 * Centralized content for recruitment urgency status display.
 * Edit this file to update copy across the platform.
 */

/**
 * Icon mapping for recruitment statuses
 */
export const RECRUITMENT_STATUS_ICONS = {
  check: CheckCircle2,
  alert: AlertTriangle,
  users: Users,
} as const;

export type RecruitmentStatusIconKey = keyof typeof RECRUITMENT_STATUS_ICONS;

export interface RecruitmentStatusConfig {
  label: string;
  description: string;
  calloutTitle: string;
  calloutDescription: string;
  color: string;
  bgColor: string;
  lightBgColor: string;
  borderColor: string;
  icon: RecruitmentStatusIconKey;
}

/**
 * Status configuration with labels, descriptions, and styling
 */
export const RECRUITMENT_STATUS_CONFIG: Record<RecruitmentStatus, RecruitmentStatusConfig> = {
  [RecruitmentStatus.OPEN]: {
    label: "OPEN",
    description: "Actively accepting CVs - Role is open and needs candidates",
    calloutTitle: "Actively Recruiting",
    calloutDescription: "This role is actively accepting CVs. Submit quality candidates to maximize your chances of earning the referral reward.",
    color: "#FF6B35",
    bgColor: "#FF6B35",
    lightBgColor: "rgba(255, 107, 53, 0.1)",
    borderColor: "rgba(255, 107, 53, 0.3)",
    icon: "check",
  },
  [RecruitmentStatus.LIMITED]: {
    label: "LIMITED",
    description: "Limited CVs needed - Only high-quality submissions required",
    calloutTitle: "Limited CVs Needed",
    calloutDescription: "Some CVs have been received. Only a few additional high-quality CVs are required. Submissions are still open but are more selective.",
    color: "#FFB800",
    bgColor: "#FFB800",
    lightBgColor: "rgba(255, 184, 0, 0.1)",
    borderColor: "rgba(255, 184, 0, 0.3)",
    icon: "alert",
  },
  [RecruitmentStatus.INTERVIEWING]: {
    label: "INTERVIEWING",
    description: "Interviews in play - Active hiring process underway",
    calloutTitle: "Interviews in Progress",
    calloutDescription: "Candidates are currently being interviewed. The hiring process is actively underway for this position.",
    color: "#007AFF",
    bgColor: "#007AFF",
    lightBgColor: "rgba(0, 122, 255, 0.1)",
    borderColor: "rgba(0, 122, 255, 0.3)",
    icon: "users",
  },
};

/**
 * Default status for jobs without recruitmentStatus field (backward compatibility)
 */
const DEFAULT_RECRUITMENT_STATUS = RecruitmentStatus.OPEN;

/**
 * Get status configuration with fallback to default
 */
export function getRecruitmentStatusConfig(status?: RecruitmentStatus): RecruitmentStatusConfig {
  if (!status) {
    return RECRUITMENT_STATUS_CONFIG[DEFAULT_RECRUITMENT_STATUS];
  }
  return RECRUITMENT_STATUS_CONFIG[status] || RECRUITMENT_STATUS_CONFIG[DEFAULT_RECRUITMENT_STATUS];
}

/**
 * Get all status options for form selectors
 */
export function getRecruitmentStatusOptions(): Array<{
  value: RecruitmentStatus;
  config: RecruitmentStatusConfig;
}> {
  return Object.entries(RECRUITMENT_STATUS_CONFIG).map(([value, config]) => ({
    value: value as RecruitmentStatus,
    config,
  }));
}

